import axios from 'axios';

// ─── Axios instance ──────────────────────────────────────────────────────────
const getBase = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'production') return 'https://api.tap2serve.co.za';
  return 'http://localhost:8080';
};

const kycApi = axios.create({ baseURL: getBase(), timeout: 20000 });

// Attach admin JWT if present
kycApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('adminToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ─── Shared types ─────────────────────────────────────────────────────────────

export type KycStatus =
  | 'PENDING'
  | 'AI_REVIEWING'
  | 'MANUAL_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'EXPIRED';

export type AdminRole =
  | 'SUPER_ADMIN'
  | 'KYC_REVIEWER'
  | 'FINANCE_ADMIN'
  | 'READ_ONLY';

export interface OnboardingChecklistDto {
  kycApproved: boolean;
  profileComplete: boolean;
  bankVerified: boolean;
  tradeCertUploaded: boolean;
  backgroundCheckPassed: boolean;
  termsAccepted: boolean;
  onboardingComplete: boolean;
  fullyComplete: boolean;
}

export interface KycQueueItem {
  submissionId: string;
  providerId: string;
  providerName: string;
  documentType: 'SA_SMART_ID' | 'SA_GREEN_ID' | 'PASSPORT';
  status: KycStatus;
  aiVerdict: string | null;
  aiConfidence: number | null;
  submittedAt: string;
  flagCount: number;
}

export interface KycDashboardStats {
  totalPending: number;
  totalApproved: number;
  totalRejected: number;
  totalManualReview: number;
  avgAiConfidence: number;
  todaySubmissions: number;
}

export interface KycSubmissionDetail {
  submissionId: string;
  providerId: string;
  status: KycStatus;
  aiVerdict: string | null;
  aiConfidence: number | null;
  aiSummary: string | null;
  aiFlags: string[];
  documentType: string;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  docFrontUrl: string | null;
  docBackUrl: string | null;
  selfieUrl: string | null;
  onboardingChecklist: OnboardingChecklistDto;
}

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: AdminRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface MfaSetupData {
  secret: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface AuditLogEntry {
  id: string;
  submissionId: string;
  actorType: 'SYSTEM' | 'ADMIN' | 'PROVIDER';
  actorId: string | null;
  action: string;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogParams {
  submissionId?: string;
  actorType?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  size?: number;
}

// ─── Queue & Dashboard ────────────────────────────────────────────────────────

/** GET /api/v1/admin/kyc/queue */
export async function getQueue(params: {
  status?: KycStatus[];
  page?: number;
  size?: number;
  sort?: string;
}): Promise<PageResponse<KycQueueItem>> {
  const res = await kycApi.get('/api/v1/admin/kyc/queue', { params });
  return res.data;
}

/** GET /api/v1/admin/kyc/dashboard-stats */
export async function getDashboardStats(): Promise<KycDashboardStats> {
  const res = await kycApi.get('/api/v1/admin/kyc/dashboard-stats');
  return res.data;
}

/** GET /api/v1/kyc/{submissionId} (admin view) */
export async function getSubmissionDetail(
  submissionId: string
): Promise<KycSubmissionDetail> {
  const res = await kycApi.get(`/api/v1/kyc/${submissionId}`);
  return res.data;
}

/** GET /api/v1/admin/kyc/{submissionId}/documents */
export async function getDocumentUrls(submissionId: string): Promise<{
  docFrontUrl: string | null;
  docBackUrl: string | null;
  selfieUrl: string | null;
}> {
  const res = await kycApi.get(`/api/v1/admin/kyc/${submissionId}/documents`);
  return res.data;
}

/** POST /api/v1/admin/kyc/{submissionId}/review */
export async function reviewSubmission(
  submissionId: string,
  decision: string,
  rejectionReason?: string
): Promise<KycSubmissionDetail> {
  const res = await kycApi.post(`/api/v1/admin/kyc/${submissionId}/review`, {
    decision,
    rejectionReason: rejectionReason ?? null,
  });
  return res.data;
}

/** POST /api/v1/admin/kyc/{submissionId}/request-resubmission */
export async function requestResubmission(
  submissionId: string,
  reason: string
): Promise<void> {
  await kycApi.post(`/api/v1/admin/kyc/${submissionId}/request-resubmission`, {
    reason,
  });
}

/**
 * Derives daily submission volumes from the queue data (7-day window).
 * Falls back to the queue endpoint filtered by date range.
 */
export async function getDailySubmissionVolumes(): Promise<
  { date: string; count: number }[]
> {
  const res = await kycApi.get('/api/v1/admin/kyc/queue', {
    params: { size: 200, sort: 'submittedAt,desc' },
  });
  const items: KycQueueItem[] = (res.data as PageResponse<KycQueueItem>).content;
  const counts: Record<string, number> = {};
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    counts[d.toISOString().split('T')[0]] = 0;
  }
  items.forEach((item) => {
    const day = item.submittedAt.split('T')[0];
    if (day in counts) counts[day] = (counts[day] ?? 0) + 1;
  });
  return Object.entries(counts).map(([date, count]) => ({ date, count }));
}

// ─── Admin user management ────────────────────────────────────────────────────

/** POST /api/v1/admin/onboarding/invite */
export async function inviteAdmin(
  email: string,
  role: AdminRole
): Promise<void> {
  await kycApi.post('/api/v1/admin/onboarding/invite', { email, role });
}

/** GET /api/v1/admin/onboarding/admins */
export async function listAdmins(
  page = 0,
  size = 20
): Promise<PageResponse<AdminUser>> {
  const res = await kycApi.get('/api/v1/admin/onboarding/admins', {
    params: { page, size },
  });
  return res.data;
}

/** DELETE /api/v1/admin/onboarding/admins/{adminId} */
export async function deactivateAdmin(adminId: string): Promise<void> {
  await kycApi.delete(`/api/v1/admin/onboarding/admins/${adminId}`);
}

// ─── MFA ──────────────────────────────────────────────────────────────────────

/** POST /api/v1/admin/onboarding/setup-mfa */
export async function setupMfa(): Promise<MfaSetupData> {
  const res = await kycApi.post('/api/v1/admin/onboarding/setup-mfa');
  return res.data;
}

/** POST /api/v1/admin/onboarding/verify-mfa */
export async function verifyMfa(totpCode: string): Promise<void> {
  await kycApi.post('/api/v1/admin/onboarding/verify-mfa', { totpCode });
}

// ─── Onboarding checklist ─────────────────────────────────────────────────────

/** GET /api/v1/admin/onboarding/onboarding-checklist/{providerId} */
export async function getOnboardingChecklist(
  providerId: string
): Promise<OnboardingChecklistDto> {
  const res = await kycApi.get(
    `/api/v1/admin/onboarding/onboarding-checklist/${providerId}`
  );
  return res.data;
}

/** POST /api/v1/admin/onboarding/onboarding-checklist/{providerId}/{field} */
export async function overrideChecklistField(
  providerId: string,
  field: string,
  value: boolean
): Promise<OnboardingChecklistDto> {
  const res = await kycApi.post(
    `/api/v1/admin/onboarding/onboarding-checklist/${providerId}/${field}`,
    { value }
  );
  return res.data;
}

/** POST /api/v1/admin/onboarding/background-check/{providerId} */
export async function initiateBackgroundCheck(
  providerId: string
): Promise<{ passed: boolean }> {
  const res = await kycApi.post(
    `/api/v1/admin/onboarding/background-check/${providerId}`
  );
  return res.data;
}

// ─── Audit log ────────────────────────────────────────────────────────────────

/**
 * Fetches KYC audit log for a specific submission.
 * GET /api/v1/kyc/{submissionId}/audit-log
 */
export async function getAuditLog(
  params: AuditLogParams
): Promise<PageResponse<AuditLogEntry>> {
  if (params.submissionId) {
    const res = await kycApi.get(
      `/api/v1/kyc/${params.submissionId}/audit-log`
    );
    const items: AuditLogEntry[] = Array.isArray(res.data) ? res.data : [];
    return {
      content: items,
      totalElements: items.length,
      totalPages: 1,
      number: 0,
      size: items.length,
    };
  }
  // General audit log endpoint (backend should implement GET /api/v1/admin/kyc/audit-log)
  const res = await kycApi.get('/api/v1/admin/kyc/audit-log', { params });
  return res.data;
}

/** POST /api/v1/admin/onboarding/accept-invite */
export async function acceptInvite(
  token: string,
  fullName: string,
  password: string
): Promise<{ id: string; email: string; fullName: string }> {
  const res = await kycApi.post('/api/v1/admin/onboarding/accept-invite', {
    token,
    fullName,
    password,
  });
  return res.data;
}
